import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface WeeklyConversationsEngagedResult {
  // Leads messaged by Sylvia (conversations where Sylvia sent welcome template)
  leadsMessaged: number;
  leadsMessagedChange: number;
  // Engaged conversations (leads messaged who replied)
  engagedCount: number;
  engagedChange: number;
  isLoading: boolean;
}

/**
 * Hook to count:
 * 1. Leads messaged by Sylvia: conversations where Sylvia sent a welcome template message
 * 2. Engaged conversations: leads messaged where the customer replied at least once
 */
export function useWeeklyConversationsEngaged(tenantId: string | null): WeeklyConversationsEngagedResult {
  const { data, isLoading } = useQuery({
    queryKey: ['weekly-conversations-engaged', tenantId],
    queryFn: async () => {
      if (!tenantId) return {
        thisWeekMessaged: 0,
        lastWeekMessaged: 0,
        thisWeekEngaged: 0,
        lastWeekEngaged: 0,
      };

      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const fourteenDaysAgo = new Date(now);
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

      // Fetch conversations from the last 14 days with their messages
      const { data: conversations, error } = await supabase
        .from('conversations')
        .select(`
          id,
          created_at,
          messages:messages!messages_conversation_id_fkey(
            id,
            direction,
            author_type,
            whatsapp_template_name,
            created_at
          )
        `)
        .eq('tenant_id', tenantId)
        .gte('created_at', fourteenDaysAgo.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Find conversations where Sylvia sent a welcome template
      const conversationsWithWelcome = (conversations || []).filter((conv) => {
        const messages = conv.messages || [];
        // Check if any message has a whatsapp_template_name (welcome message from Sylvia)
        return messages.some((msg) =>
          msg.whatsapp_template_name &&
          msg.direction === 'outbound' &&
          msg.author_type === 'bot'
        );
      });

      // From those, find ones where customer replied
      const engagedConversations = conversationsWithWelcome.filter((conv) => {
        const messages = conv.messages || [];
        return messages.some((msg) => msg.direction === 'inbound');
      });

      // Split into this week and last week for messaged leads
      const thisWeekMessaged = conversationsWithWelcome.filter((conv) => {
        const convDate = new Date(conv.created_at);
        return convDate >= sevenDaysAgo && convDate <= now;
      }).length;

      const lastWeekMessaged = conversationsWithWelcome.filter((conv) => {
        const convDate = new Date(conv.created_at);
        return convDate >= fourteenDaysAgo && convDate < sevenDaysAgo;
      }).length;

      // Split into this week and last week for engaged
      const thisWeekEngaged = engagedConversations.filter((conv) => {
        const convDate = new Date(conv.created_at);
        return convDate >= sevenDaysAgo && convDate <= now;
      }).length;

      const lastWeekEngaged = engagedConversations.filter((conv) => {
        const convDate = new Date(conv.created_at);
        return convDate >= fourteenDaysAgo && convDate < sevenDaysAgo;
      }).length;

      return {
        thisWeekMessaged,
        lastWeekMessaged,
        thisWeekEngaged,
        lastWeekEngaged,
      };
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    leadsMessaged: data?.thisWeekMessaged ?? 0,
    leadsMessagedChange: (data?.thisWeekMessaged ?? 0) - (data?.lastWeekMessaged ?? 0),
    engagedCount: data?.thisWeekEngaged ?? 0,
    engagedChange: (data?.thisWeekEngaged ?? 0) - (data?.lastWeekEngaged ?? 0),
    isLoading,
  };
}
